import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    PasswordInput,
    BooleanInput,
    ReferenceInput,
    SelectInput,
    required,
    email,
    minLength,
    regex,
    Toolbar,
    SaveButton,
    useRedirect,
    useTranslate,
    useNotify,
    useGetIdentity,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Typography, Alert, Card, CardContent, IconButton, Button } from '@mui/material';
import { Person, Security, Info, ArrowBack, Business, AutoAwesome } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const USER_KNOWN_FIELDS = new Set([
    'email', 'username', 'firstName', 'lastName', 'password',
    'phone', 'roleId', 'vendorId', 'isActive', 'language',
]);

const mapUserBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const userFieldLabelKey = (field: string): string =>
    USER_KNOWN_FIELDS.has(field)
        ? `resources.backend-users.fields.${field}`
        : field;

// Crypto-strong random index in [0, max). Falls back to Math.random in the
// extremely unlikely case crypto isn't available (very old browsers).
const randomIndex = (max: number): number => {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
        const buf = new Uint32Array(1);
        window.crypto.getRandomValues(buf);
        return buf[0] % max;
    }
    return Math.floor(Math.random() * max);
};

// Generate a 16-char password that satisfies the backend complexity regex.
// Avoids visually confusable chars (I/l/1, O/0) per the same convention used
// for license plates elsewhere in the app.
const generateStrongPassword = (): string => {
    const lower = 'abcdefghijkmnpqrstuvwxyz';     // no l
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';     // no I, O
    const digit = '23456789';                      // no 0, 1
    const special = '!@#$%^&*?-_+=';
    const all = lower + upper + digit + special;

    // Guarantee one of each required class
    const out: string[] = [
        lower[randomIndex(lower.length)],
        upper[randomIndex(upper.length)],
        digit[randomIndex(digit.length)],
        special[randomIndex(special.length)],
    ];
    while (out.length < 16) out.push(all[randomIndex(all.length)]);

    // Fisher-Yates shuffle so the required chars aren't always at the front
    for (let i = out.length - 1; i > 0; i--) {
        const j = randomIndex(i + 1);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out.join('');
};

// Wraps the password input with a "Generate" button. Lives inside <SimpleForm>
// so it can read/write the form state via react-hook-form's useFormContext.
const PasswordWithGenerate: React.FC<{ validate: any }> = ({ validate }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { setValue } = useFormContext();

    const handleGenerate = async () => {
        const pwd = generateStrongPassword();
        setValue('password', pwd, { shouldValidate: true, shouldDirty: true });
        try {
            await navigator.clipboard?.writeText(pwd);
            notify(
                translate('resources.backend-users.create.passwordGeneratedCopied', {
                    _: 'Password generated and copied to clipboard',
                }),
                { type: 'success', autoHideDuration: 3000 }
            );
        } catch {
            notify(
                translate('resources.backend-users.create.passwordGenerated', {
                    _: 'Password generated',
                }),
                { type: 'success', autoHideDuration: 3000 }
            );
        }
    };

    return (
        <Box>
            {/*
                Move the helper text out of PasswordInput (helperText={false}) and
                render it ourselves below the row. This way the flex row contains
                only the input box + the button, and `alignItems: center` actually
                centers them against each other instead of including the helper-text
                area in the vertical-centering calculation.
            */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <PasswordInput
                        source="password"
                        label={translate('resources.backend-users.create.temporaryPassword')}
                        validate={validate}
                        fullWidth
                        helperText={false}
                    />
                </Box>
                <Button
                    onClick={handleGenerate}
                    variant="outlined"
                    startIcon={<AutoAwesome />}
                    sx={{
                        whiteSpace: 'nowrap',
                        textTransform: 'none',
                        flexShrink: 0,
                    }}
                >
                    {translate('resources.backend-users.create.generatePassword', {
                        _: 'Generate',
                    })}
                </Button>
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5, ml: 1.75 }}
            >
                {translate('resources.backend-users.create.passwordHelperText')}
            </Typography>
        </Box>
    );
};

const CreateToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/backend-users')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.backend-users.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5 }}>
                    {translate('resources.backend-users.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.backend-users.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const UserCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const { data: identity } = useGetIdentity();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    // Vendor-scope: vendor-scoped actors can only create users under their own
    // vendor(s). With a single vendor we skip the visible picker entirely —
    // the vendorId is auto-injected via the create transform. With multiple
    // vendors we still filter the dropdown. Super-admins see everything.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const isSingleVendor = isVendorScoped && actorVendorIds.length === 1;
    const defaultVendorId = isVendorScoped ? actorVendorIds[0] : undefined;

    const transform = (data: any) => {
        const next = { ...data };
        // Pin vendorId for scoped actors — defense against a tampered submission
        // that omits/overwrites the field. Backend enforces too but this keeps
        // the client consistent with the UX (auto-assigned vendor).
        if (isVendorScoped && !next.vendorId) {
            next.vendorId = actorVendorIds[0];
        }
        // Strip vendorRole if no vendorId — backend rejects role-without-vendor
        // via superRefine. Stripping here avoids a confusing 400 when the admin
        // leaves the optional vendor blank but the role default is still set.
        if (!next.vendorId) {
            delete next.vendorRole;
        }
        return next;
    };

    const validateEmail = [required(), email()];
    const validateUsername = [required(), minLength(3)];
    const validatePassword = [
        required(),
        minLength(8),
        regex(/[A-Z]/, translate('resources.backend-users.create.passwordValidation.uppercase')),
        regex(/[a-z]/, translate('resources.backend-users.create.passwordValidation.lowercase')),
        regex(/[0-9]/, translate('resources.backend-users.create.passwordValidation.number')),
        regex(/[^A-Za-z0-9]/, translate('resources.backend-users.create.passwordValidation.special')),
    ];

    return (
        <Create
            title={false}
            component="div"
            redirect={false}
            mutationMode="pessimistic"
            transform={transform}
            mutationOptions={{
                onSuccess: (data: any) => {
                    setServerErrors({});
                    notify('resources.backend-users.notifications.createSuccess', {
                        type: 'success',
                        autoHideDuration: 3000
                    });
                    if (data?.id) {
                        redirect('edit', 'backend-users', data.id);
                    } else {
                        redirect('list', 'backend-users');
                    }
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapUserBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(userFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    padding: { xs: 0, sm: 'inherit' },
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: { xs: 2, sm: 3 } }}>
                        <Typography variant="body2">
                            {translate('resources.backend-users.create.infoMessage')}
                        </Typography>
                    </Alert>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Person sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.create.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="username"
                                    validate={validateUsername}
                                    fullWidth
                                    helperText={translate('resources.backend-users.create.usernameHelperText')}
                                />

                                <TextInput
                                    source="email"
                                    validate={validateEmail}
                                    fullWidth
                                    helperText={translate('resources.backend-users.create.emailHelperText')}
                                />

                                <TextInput
                                    source="firstName"
                                    validate={required()}
                                    fullWidth
                                />

                                <TextInput
                                    source="lastName"
                                    validate={required()}
                                    fullWidth
                                />

                                <ReferenceInput
                                    source="roleId"
                                    reference="roles"
                                >
                                    <SelectInput
                                        optionText="name"
                                        fullWidth
                                        validate={required()}
                                        helperText={translate('resources.backend-users.create.roleHelperText')}
                                    />
                                </ReferenceInput>

                                <SelectInput
                                    source="language"
                                    choices={[
                                        { id: 'en', name: translate('language.en') },
                                        { id: 'zh-TW', name: translate('language.zh-TW') },
                                    ]}
                                    defaultValue="en"
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Vendor Assignment Section (Optional) */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.create.vendorAssignment')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                {/*
                                  Vendor picker: hidden entirely for single-vendor
                                  scoped actors — the vendorId is auto-injected via
                                  the create transform, so surfacing a disabled
                                  dropdown with one option is just UI noise. Multi-
                                  vendor scoped actors still see a filtered picker;
                                  super-admin sees every vendor.
                                */}
                                {!isSingleVendor && (
                                    <ReferenceInput
                                        source="vendorId"
                                        reference="vendors"
                                        filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                                    >
                                        <SelectInput
                                            optionText="name"
                                            fullWidth
                                            defaultValue={defaultVendorId}
                                            helperText={translate('resources.backend-users.create.vendorHelperText')}
                                        />
                                    </ReferenceInput>
                                )}

                                {/*
                                  Vendor role picker. Backend now requires an
                                  explicit `vendorRole` whenever `vendorId` is
                                  present (see createUserSchema.superRefine) —
                                  no more silent default to 'operator'. The
                                  defaultValue here is a UI affordance, not a
                                  silent backend coercion.
                                */}
                                <SelectInput
                                    source="vendorRole"
                                    label={translate('resources.backend-users.fields.vendorRole', { _: 'Vendor role' })}
                                    choices={[
                                        { id: 'viewer', name: translate('resources.backend-users.vendorRoles.viewer', { _: 'Viewer' }) },
                                        { id: 'operator', name: translate('resources.backend-users.vendorRoles.operator', { _: 'Operator' }) },
                                        { id: 'manager', name: translate('resources.backend-users.vendorRoles.manager', { _: 'Manager' }) },
                                        { id: 'admin', name: translate('resources.backend-users.vendorRoles.admin', { _: 'Admin' }) },
                                    ]}
                                    defaultValue="operator"
                                    fullWidth
                                    helperText={translate('resources.backend-users.create.vendorRoleHelperText', { _: 'Permission level within the assigned vendor' })}
                                />

                                <Alert severity="info">
                                    <Typography variant="caption">
                                        {translate('resources.backend-users.create.vendorAssignmentInfo')}
                                    </Typography>
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Security Settings Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Security sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.backend-users.create.securitySettings')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <PasswordWithGenerate validate={validatePassword} />

                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.backend-users.edit.activeAccount')}
                                    defaultValue={true}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <BooleanInput
                                    source="requirePasswordChange"
                                    label={translate('resources.backend-users.fields.requirePasswordChange')}
                                    defaultValue={true}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Info sx={{ fontSize: 20, mr: 1, color: '#1976D2' }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {translate('resources.backend-users.create.twoFactorInfo')}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('resources.backend-users.create.twoFactorHelperText')}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};